const SCORE_MAX_POINTS = 100;
const SCORE_SPEED_FACTOR = 0.01;
const SCORE_ACCURACY_FACTOR = 0.5;
const SCORE_MIN_POINTS = 10;

export function getCorrectAnswer(answerData, questionId, items) {
  const answer = answerData?.find(a => a.transaction_id === questionId);
  if (!answer) return null;

  const drParent = items.find(item => item.id === answer.dr[1]);
  const crParent = items.find(item => item.id === answer.cr[1]);

  const crIsRevenue = crParent?.name === 'Revenues';
  const drIsRevenueOrExpense = drParent?.name === 'Revenues' || drParent?.name === 'Expenses';

  if (crIsRevenue) return 'Increase Profits';
  if (drIsRevenueOrExpense) return 'Decrease Profits';
  return 'No Impact';
}

export function validateDecision(answerData, questionId, selectedOption, items) {
  const correctAnswer = getCorrectAnswer(answerData, questionId, items);
  return correctAnswer === selectedOption;
}

// Returns the account_item ids the view api marks as correct for this
// transaction's framework. Accepts either bare ids (`[5, 8]`) or objects
// with an `id` field (`[{id: 5}, {id: 8}]`).
export function getApcFrameworkIds(answerData, transactionId) {
  const answer = answerData?.find(a => a.transaction_id === transactionId);
  const raw = Array.isArray(answer?.framework) ? answer.framework : [];
  return raw
    .map(entry => (entry != null && typeof entry === 'object' ? entry.id : entry))
    .filter(id => id != null);
}

// Validates by id: the clicked option's account_item.id must be present in
// the framework for the transaction.
export function validateApcDecision(answerData, transactionId, selectedOptionId) {
  if (selectedOptionId == null) return false;
  return getApcFrameworkIds(answerData, transactionId).includes(selectedOptionId);
}

export function calculateScore({ timeElapsedSeconds, totalTries }) {
  const speedImpact = Math.max(0, 1 - SCORE_SPEED_FACTOR * timeElapsedSeconds);
  const accuracyImpact = totalTries === 1 ? 1 : Math.pow(SCORE_ACCURACY_FACTOR, totalTries);
  return Math.max(SCORE_MAX_POINTS * speedImpact * accuracyImpact, SCORE_MIN_POINTS);
}

const SESSION_STORAGE_PREFIX = 'principles_session_';

export function saveSessionToStorage(gameSessionId, sessionData) {
  const key = `${SESSION_STORAGE_PREFIX}${gameSessionId}`;
  localStorage.setItem(key, JSON.stringify(sessionData));
}

export function loadSessionFromStorage(gameSessionId) {
  const key = `${SESSION_STORAGE_PREFIX}${gameSessionId}`;
  const data = localStorage.getItem(key);
  return data ? JSON.parse(data) : null;
}

export function clearSessionFromStorage(gameSessionId) {
  const key = `${SESSION_STORAGE_PREFIX}${gameSessionId}`;
  localStorage.removeItem(key);
}

export function formatSecondsToTimeString(totalSeconds) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function formatDecisionsForBulkPost(decisions, gameTimeElapsedSeconds) {
  // Fallback for legacy decisions that don't carry their own
  // game_time_elapsed; otherwise use the per-decision cumulative value
  // so the field grows monotonically across rows.
  const fallbackGameTimeElapsed = formatSecondsToTimeString(gameTimeElapsedSeconds);

  return decisions.map(d => {
    const base = {
      game: d.game,
      game_session: d.game_session,
      question: d.question,
      time_elapsed: formatSecondsToTimeString(d.time_elapsed),
      game_time_elapsed: d.game_time_elapsed != null
        ? formatSecondsToTimeString(d.game_time_elapsed)
        : fallbackGameTimeElapsed,
      timestamp: d.timestamp
    };
    if (d.selected_option_item != null) {
      return { ...base, selected_option_item: d.selected_option_item };
    }
    return { ...base, selected_option: d.selected_option };
  });
}
